<template>
    <div class="carousel-wrapper">
        <div class="carousel-container" @mouseenter="pauseScroll" @mouseleave="resumeScroll" @mousedown="startDrag"
            @mousemove="onDrag" @mouseup="endDrag" @touchstart="startTouch" @touchmove="onTouch" @touchend="endTouch"
            @wheel="onWheel" ref="scrollContainer">
            <div class="carousel-track" ref="track" :style="trackStyle">
                <div class="carousel-item" v-for="(logo, index) in displayLogos" :key="`${logo}-${index}`"
                    :class="getItemClass(index)" :style="getItemStyle(index)">
                    <img :src="logo" alt="logo" loading="lazy" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "TianshengCarousel",
    props: {
        logos: {
            type: Array,
            default: () => [],
        },
        scrollSpeed: {
            type: Number,
            default: 1,
            validator: (value) => value > 0 && value <= 10
        },
        scrollInterval: {
            type: Number,
            default: 16, // 约60fps
            validator: (value) => value >= 10 && value <= 100
        },
        enableManualScroll: {
            type: Boolean,
            default: true
        },
        enableAutoScroll: {
            type: Boolean,
            default: true
        },
        rows: {
            type: Number,
            default: 1,
            validator: (value) => value >= 1 && value <= 10
        },
        staggered: {
            type: Boolean,
            default: false
        },
        staggerOffset: {
            type: Number,
            default: 30,
            validator: (value) => value >= 0 && value <= 100
        }
    },
    data() {
        return {
            animationFrameId: null,
            isDragging: false,
            isManualScrolling: false,
            lastMouseX: 0,
            lastTouchX: 0,
            autoScrollPaused: false,
            manualScrollTimeout: null,
            currentTransform: 0,
            containerWidth: 1200,
            itemWidth: 140, // 120 + 20 margin
            singleSetWidth: 0,
            isInitialized: false,
            lastTime: 0,
        };
    },
    computed: {
        displayLogos() {
            if (!this.logos || this.logos.length === 0) return [];

            // 优化：减少不必要的复制
            const minItemsForScroll = this.calculateMinItemsForScroll();
            const repeatTimes = Math.max(2, Math.ceil(minItemsForScroll / this.logos.length));

            // 使用缓存避免重复计算
            if (this._cachedLogos &&
                this._cachedRepeatTimes === repeatTimes &&
                this._cachedOriginalLength === this.logos.length) {
                return this._cachedLogos;
            }

            let repeatedLogos = [];
            for (let i = 0; i < repeatTimes; i++) {
                repeatedLogos = repeatedLogos.concat(this.logos);
            }

            // 缓存结果
            this._cachedLogos = repeatedLogos;
            this._cachedRepeatTimes = repeatTimes;
            this._cachedOriginalLength = this.logos.length;

            return repeatedLogos;
        },
        trackStyle() {
            const baseStyle = {
                transform: `translate3d(${this.currentTransform}px, 0, 0)`,
                willChange: 'transform',
            };

            if (this.rows > 1) {
                return {
                    ...baseStyle,
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: 'column',
                    height: `${this.rows * 70}px`, // 每行5rem（3.75rem项目 + 1.25rem间距）
                    width: 'max-content',
                    alignContent: 'flex-start',
                    alignItems: 'flex-start',
                };
            } else {
                return {
                    ...baseStyle,
                    display: 'flex',
                    width: 'max-content',
                    alignItems: 'center',
                };
            }
        },
        itemsPerRow() {
            return Math.floor(this.containerWidth / this.itemWidth);
        }
    },
    watch: {
        scrollSpeed() {
            // 防抖优化
            this.debounceRestart();
        },
        scrollInterval() {
            this.debounceRestart();
        },
        enableAutoScroll(newVal) {
            if (newVal && !this.autoScrollPaused) {
                this.startAutoScroll();
            } else {
                this.stopAutoScroll();
            }
        },
        rows() {
            this.resetAndRestart();
        },
        logos: {
            handler() {
                // 清除缓存
                this._cachedLogos = null;
                this.resetAndRestart();
            },
            deep: true
        },
        staggered() {
            this.resetAndRestart();
        }
    },
    mounted() {
        this.initializeComponent();
    },
    beforeDestroy() {
        this.cleanup();
    },
    methods: {
        // 初始化组件
        initializeComponent() {
            this.$nextTick(() => {
                this.updateContainerWidth();
                this.calculateSingleSetWidth();

                if (this.enableAutoScroll) {
                    this.startAutoScroll();
                }

                this.isInitialized = true;

                // 防止图片拖拽
                if (this.$refs.scrollContainer) {
                    this.$refs.scrollContainer.addEventListener('dragstart', this.preventDragStart);
                }

                // 监听窗口大小变化
                window.addEventListener('resize', this.handleResize);
            });
        },

        // 清理资源
        cleanup() {
            this.stopAutoScroll();

            if (this.manualScrollTimeout) {
                clearTimeout(this.manualScrollTimeout);
            }

            if (this.$refs.scrollContainer) {
                this.$refs.scrollContainer.removeEventListener('dragstart', this.preventDragStart);
            }

            window.removeEventListener('resize', this.handleResize);
        },

        // 处理窗口大小变化
        handleResize() {
            this.debounce(() => {
                this.updateContainerWidth();
                this.calculateSingleSetWidth();
            }, 250)();
        },

        // 更新容器宽度
        updateContainerWidth() {
            if (this.$refs.scrollContainer) {
                this.containerWidth = this.$refs.scrollContainer.clientWidth;
            }
        },

        // 计算单个循环的宽度
        calculateSingleSetWidth() {
            if (!this.isInitialized || !this.$refs.track) return;

            const track = this.$refs.track;
            const repeatTimes = Math.ceil(this.displayLogos.length / this.logos.length);
            this.singleSetWidth = track.scrollWidth / repeatTimes;
        },

        // 获取项目的CSS类
        getItemClass(index) {
            const classes = ['carousel-item'];

            if (this.staggered && this.rows > 1) {
                const row = index % this.rows;
                classes.push(`staggered-row-${row}`);
            }

            return classes;
        },

        // 获取项目的内联样式
        getItemStyle(index) {
            const style = {};

            if (this.staggered && this.rows > 1) {
                const row = index % this.rows;
                if (row % 2 === 1) {
                    style.marginLeft = `${this.staggerOffset}px`;
                }
            }

            return style;
        },

        // 计算支持滚动所需的最小图片数量
        calculateMinItemsForScroll() {
            const baseItems = this.itemsPerRow * (this.rows === 1 ? 3 : 2);
            return Math.max(baseItems, 20); // 最少20个项目确保流畅滚动
        },

        // 优化的自动滚动
        startAutoScroll() {
            if (!this.enableAutoScroll || this.animationFrameId) return;

            const animate = (currentTime) => {
                // 使用高精度时间控制
                if (currentTime - this.lastTime >= this.scrollInterval) {
                    if (!this.autoScrollPaused && !this.isManualScrolling) {
                        this.updateScrollPosition();
                    }
                    this.lastTime = currentTime;
                }

                if (this.enableAutoScroll) {
                    this.animationFrameId = requestAnimationFrame(animate);
                }
            };

            this.animationFrameId = requestAnimationFrame(animate);
        },

        // 更新滚动位置
        updateScrollPosition() {
            if (!this.singleSetWidth) {
                this.calculateSingleSetWidth();
                if (!this.singleSetWidth) return;
            }

            this.currentTransform -= this.scrollSpeed;

            // 优化的边界处理
            if (Math.abs(this.currentTransform) >= this.singleSetWidth) {
                this.currentTransform += this.singleSetWidth;
            }
        },

        stopAutoScroll() {
            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }
        },

        // 防抖重启
        debounceRestart() {
            if (this._restartTimeout) {
                clearTimeout(this._restartTimeout);
            }

            this._restartTimeout = setTimeout(() => {
                this.restartAutoScroll();
            }, 100);
        },

        restartAutoScroll() {
            this.stopAutoScroll();
            if (this.enableAutoScroll && !this.autoScrollPaused) {
                this.startAutoScroll();
            }
        },

        resetAndRestart() {
            this.currentTransform = 0;
            this.singleSetWidth = 0;
            this.$nextTick(() => {
                this.calculateSingleSetWidth();
                this.restartAutoScroll();
            });
        },

        pauseScroll() {
            this.autoScrollPaused = true;
        },

        resumeScroll() {
            if (!this.isManualScrolling) {
                this.autoScrollPaused = false;
            }
        },

        // 优化的拖拽处理
        startDrag(event) {
            if (!this.enableManualScroll) return;

            this.isDragging = true;
            this.isManualScrolling = true;
            this.lastMouseX = event.clientX;
            this.autoScrollPaused = true;

            event.preventDefault();
            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.endDrag);
        },

        onDrag(event) {
            if (!this.isDragging || !this.enableManualScroll) return;

            const deltaX = this.lastMouseX - event.clientX;
            this.currentTransform -= deltaX;
            this.lastMouseX = event.clientX;

            this.handleTransformBoundary();
            event.preventDefault();
        },

        endDrag() {
            if (!this.isDragging) return;

            this.isDragging = false;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.endDrag);
            this.endManualScroll();
        },

        // 触摸处理
        startTouch(event) {
            if (!this.enableManualScroll) return;

            this.isManualScrolling = true;
            this.lastTouchX = event.touches[0].clientX;
            this.autoScrollPaused = true;
        },

        onTouch(event) {
            if (!this.isManualScrolling || !this.enableManualScroll) return;

            const deltaX = this.lastTouchX - event.touches[0].clientX;
            this.currentTransform -= deltaX;
            this.lastTouchX = event.touches[0].clientX;

            this.handleTransformBoundary();
            event.preventDefault();
        },

        endTouch() {
            this.endManualScroll();
        },

        // 滚轮处理
        onWheel(event) {
            if (!this.enableManualScroll) return;

            this.isManualScrolling = true;
            this.autoScrollPaused = true;

            this.currentTransform -= event.deltaY * 0.5; // 减少滚轮敏感度
            this.handleTransformBoundary();

            this.endManualScroll();
            event.preventDefault();
        },

        // 处理变换边界
        handleTransformBoundary() {
            if (!this.singleSetWidth) return;

            if (Math.abs(this.currentTransform) >= this.singleSetWidth) {
                this.currentTransform += this.singleSetWidth;
            } else if (this.currentTransform > 0) {
                this.currentTransform -= this.singleSetWidth;
            }
        },

        // 结束手动滚动
        endManualScroll() {
            if (this.manualScrollTimeout) {
                clearTimeout(this.manualScrollTimeout);
            }

            this.manualScrollTimeout = setTimeout(() => {
                this.isManualScrolling = false;
                this.autoScrollPaused = false;
            }, 800); // 减少延迟
        },

        // 工具函数
        preventDragStart(event) {
            event.preventDefault();
        },

        debounce(func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },

        // 公共方法
        scrollTo(position) {
            this.currentTransform = -position;
            this.handleTransformBoundary();
        },

        getScrollPosition() {
            return Math.abs(this.currentTransform);
        }
    },
};
</script>

<style scoped>
.carousel-wrapper {
    width: 100%;
    height: 100%;
    user-select: none;
    contain: layout style paint;
}

.carousel-container {
    width: 100%;
    overflow: hidden;
    position: relative;
    cursor: grab;
    contain: layout style paint;
}

.carousel-container:active {
    cursor: grabbing;
}

.carousel-track {
    backface-visibility: hidden;
    perspective: 62.5rem;
    transform-style: preserve-3d;
}

.carousel-item {
    flex-shrink: 0;
    border: .0625rem dashed #ccc;
    padding: .625rem;
    width: 7.5rem;
    height: 3.75rem;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
    background: rgba(255, 255, 255, 0.9);
    border-radius: .5rem;
    box-shadow: 0 .125rem .5rem rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
    backface-visibility: hidden;
    transform-style: preserve-3d;
    contain: layout style paint;
}

.carousel-item:hover {
    transform: translateY(-0.125rem) translateZ(0);
    box-shadow: 0 .25rem .75rem rgba(0, 0, 0, 0.15);
}

.carousel-item img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    pointer-events: none;
    backface-visibility: hidden;
}

/* 移动端优化 */
@media (max-width: 48rem) {
    .carousel-container {
        cursor: default;
    }

    .carousel-item {
        width: 6.75rem;
        height: 3.125rem;
        margin-right: .9375rem;
        margin-bottom: .6rem;
    }

    .carousel-item:hover,
    .staggered-row-1:hover,
    .staggered-row-2:hover,
    .staggered-row-3:hover {
        transform: none;
    }
}

/* 性能优化 */
@media (prefers-reduced-motion: reduce) {

    .carousel-item,
    .carousel-item:hover,
    .staggered-row-1,
    .staggered-row-2,
    .staggered-row-3,
    .staggered-row-1:hover,
    .staggered-row-2:hover,
    .staggered-row-3:hover {
        transition: none;
        transform: none;
    }
}
</style>
